"use client";

import { useEffect } from "react";

import { getOneTrustConsent, subscribeToOneTrustConsent } from "@up/analytics";
import { ownerInit, profileInit, useTheme } from "@up/data";

import { brazeDisable, brazeEnable } from "./braze-init";
import { datadogDisable, datadogEnable, datadogInit } from "./datadog-init";
import { heapDisable, heapEnable, heapLoaderInit } from "./heap-loader";

import type { OneTrustConsent } from "@up/analytics";

interface ConsentGate {
  category: keyof OneTrustConsent;
  enable: () => void;
  disable: () => void;
}

// Each tool's OneTrust category must match how its cookies are categorized in
// the OneTrust cookie list.
const CONSENT_GATES: readonly ConsentGate[] = [
  // Marketing engagement (in-app messaging, campaigns); cookies ab.storage.*.
  {
    category: "targetingCookies",
    enable: brazeEnable,
    disable: brazeDisable,
  },
  // Front-end error logging; session cookie _dd_s / _dd_s_v2.
  {
    category: "performanceCookies",
    enable: datadogEnable,
    disable: datadogDisable,
  },
];

export const FrontendInit = () => {
  const theme = useTheme();

  useEffect(() => {
    heapLoaderInit(process.env.NEXT_PUBLIC_HEAP_ID);
    if (theme.analyticsPartnerName) {
      datadogInit();
    }

    profileInit();
    ownerInit();

    // Gate Heap on OneTrust performance/analytics consent. Subscribe
    // imperatively (not via a reactive effect) so a revocation flips Heap's
    // kill-switch synchronously inside the OneTrustGroupsUpdated dispatch,
    // before Heap's timers can fire again — a React render + passive effect
    // would leave a window where tracking continues after consent is withdrawn.
    //
    // Fail closed: only an explicit grant (1) keeps Heap enabled. Every other
    // state disables it, including -1 (not yet collected), where heapDisable()
    // safely no-ops while the loader is idle.
    const syncHeapConsent = () => {
      const consent = getOneTrustConsent();
      console.log("syncHeapConsent: ", consent);
      if (consent.performanceCookies === 1) {
        heapEnable();
      } else {
        heapDisable();
      }
    };
    syncHeapConsent();

    // Gate Braze and Datadog the same way. Only an explicit grant (1) enables a
    // tool, and an explicit denial (0) disables it and purges its storage.
    // While OneTrust hasn't published a decision yet (-1) the tools stay
    // dormant but their storage is left alone, so a returning visitor who
    // consented doesn't get a fresh identity on every page load before
    // OneTrust boots.
    const syncConsentGates = () => {
      const consent = getOneTrustConsent();
      for (const { category, enable, disable } of CONSENT_GATES) {
        if (consent[category] === 1) {
          enable();
        } else if (consent[category] === 0) {
          disable();
        }
      }
    };
    syncConsentGates();

    const unsubscribeHeap = subscribeToOneTrustConsent(syncHeapConsent);
    const unsubscribeGates = subscribeToOneTrustConsent(syncConsentGates);

    return () => {
      unsubscribeHeap();
      unsubscribeGates();
    };
  }, []);

  return null;
};
